import React, { useState } from 'react';
import { VanityPage } from '../types';

interface TemplateContactProps {
    page: VanityPage;
}

const TemplateContact: React.FC<TemplateContactProps> = ({ page }) => {
    // State management for dynamic form functionality
    const [formData, setFormData] = useState({
        name: '',
        company: '',
        email: '',
        message: ''
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            // Integration point for your API
            console.log('Inquiry submitted:', formData);

            // Artificial delay to show loading state
            await new Promise(resolve => setTimeout(resolve, 1000));
            setIsSubmitted(true);
        } catch (error) {
            console.error('Error submitting inquiry:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isSubmitted) {
        return (
            <div className="pt-40 pb-40 fade-in min-h-[60vh] flex flex-col items-center justify-center bg-vanity-white text-center px-4">
                <h2 className="font-serif text-5xl md:text-6xl text-vanity-black mb-6">Vielen Dank.</h2>
                <p className="font-sans text-xl text-vanity-grey mb-12">Wir haben Ihre Nachricht erhalten und melden uns in Kürze.</p>
                <button
                    onClick={() => setIsSubmitted(false)}
                    className="font-sans text-[11px] uppercase tracking-[0.2em] border-b border-vanity-black pb-1 hover:text-vanity-gold transition-colors"
                >
                    Zurück zum Formular
                </button>
            </div>
        );
    }

    return (
        <div className="pt-32 pb-32 fade-in">
            <div className="max-w-[1440px] mx-auto px-8 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-20">

                    {/* Intro Text - Structure and Styles Locked 1:1 */}
                    <div className="lg:col-span-5">
                        <span className="font-sans text-[11px] uppercase tracking-[0.4em] text-vanity-grey mb-6 block">
                            Kontaktanfrage
                        </span>
                        <h1 className="font-serif text-3xl md:text-5xl text-vanity-black mb-10">
                            {page.contentModules && page.contentModules.length > 0 && page.contentModules[0].title
                                ? page.contentModules[0].title
                                : "Eine professionelle Einladung."}
                        </h1>
                        <div className="space-y-8 prose font-sans text-vanity-black/70 leading-relaxed text-lg">
                            {/* Render content modules if available, otherwise fallback to German text */}
                            {page.contentModules && page.contentModules.length > 0 ? (
                                page.contentModules.map((module, idx) => (
                                    <div key={idx} dangerouslySetInnerHTML={{ __html: module.text || '' }} />
                                ))
                            ) : (
                                <>
                                    <p>
                                        Wir glauben an Partnerschaften, die die Intelligenz unserer Leser respektieren. Wenn Ihre Marke mit unseren Werten von klinischer Integrität und ästhetischer Raffinesse übereinstimmt, laden wir Sie ein, ein Gespräch zu beginnen.
                                    </p>
                                    <p>
                                        Unser Team bietet maßgeschneiderte Medienkits und Platzierungsstrategien für anspruchsvolle Werbetreibende aus den Bereichen Medizin, Schönheit und Luxus.
                                    </p>
                                </>
                            )}
                        </div>

                        <div className="mt-16 pt-12 border-t border-vanity-charcoal/10">
                            <h3 className="font-sans text-[11px] uppercase tracking-widest text-vanity-black mb-6">Direkter Kontakt</h3>
                            <p className="font-sans text-xl text-vanity-black">office@vanity.co.at</p>
                            <p className="font-sans text-xl text-vanity-black mt-2">+43 664 1000 118</p>
                        </div>
                    </div>

                    {/* Contact Form - Structure and Styles Locked 1:1 */}
                    <div className="lg:col-span-6 lg:offset-1">
                        <div className="bg-vanity-white border border-vanity-charcoal/5 p-12 md:p-16">
                            <form onSubmit={handleSubmit} className="space-y-12">
                                <div className="space-y-8">
                                    <div className="group relative">
                                        <label className="font-sans text-[11px] uppercase tracking-widest text-vanity-black mb-2 block">Name</label>
                                        <input
                                            required
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Ihr Name"
                                            className="w-full bg-transparent border-b border-vanity-charcoal/20 py-4 focus:outline-none focus:border-vanity-gold font-serif text-lg transition-colors placeholder:text-vanity-grey/30"
                                        />
                                    </div>
                                    <div className="group relative">
                                        <label className="font-sans text-[11px] uppercase tracking-widest text-vanity-black mb-2 block">Firma</label>
                                        <input
                                            type="text"
                                            name="company"
                                            value={formData.company}
                                            onChange={handleChange}
                                            placeholder="Firma / Brand"
                                            className="w-full bg-transparent border-b border-vanity-charcoal/20 py-4 focus:outline-none focus:border-vanity-gold font-serif text-lg transition-colors placeholder:text-vanity-grey/30"
                                        />
                                    </div>
                                    <div className="group relative">
                                        <label className="font-sans text-[11px] uppercase tracking-widest text-vanity-black mb-2 block">Email</label>
                                        <input
                                            required
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="Ihre E-Mail"
                                            className="w-full bg-transparent border-b border-vanity-charcoal/20 py-4 focus:outline-none focus:border-vanity-gold font-serif text-lg transition-colors placeholder:text-vanity-grey/30"
                                        />
                                    </div>
                                    <div className="group relative">
                                        <label className="font-sans text-[11px] uppercase tracking-widest text-vanity-black mb-2 block">Anfrage</label>
                                        <textarea
                                            required
                                            rows={4}
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            placeholder="Ihre Nachricht"
                                            className="w-full bg-transparent border-b border-vanity-charcoal/20 py-4 focus:outline-none focus:border-vanity-gold font-serif text-lg transition-colors resize-none placeholder:text-vanity-grey/30"
                                        ></textarea>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className={`w-full font-sans text-[13px] uppercase tracking-[0.2em] border border-vanity-charcoal py-6 transition-all duration-300 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-vanity-black hover:text-vanity-white'
                                        }`}
                                >
                                    {isLoading ? 'Verarbeiten...' : 'Anfrage Senden'}
                                </button>
                            </form>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default TemplateContact;